// RecipientChip — pill with Avatar + name + remove × used in the bulk-request
// recipient bar.
//
// Mirrors `.ra-recip-chip` and its sub-classes in
// /Users/harry/Downloads/employee-portal/project/styles.css (lines 2521–2546)
// and the JSX from /Users/harry/Downloads/employee-portal/project/bulk-request.jsx
// (lines 376–399).
//
// Structure:
//   [Avatar | GroupAvatar]  Name  (Myself|N members)  ✕
//
// Padding is asymmetric: 5px on top/bottom, 12px right (text side), 6px left
// (avatar side, since the avatar already has visual mass). This literal padding
// is intentionally inlined — there is no theme token at 5px.

import { Box } from "@chakra-ui/react";
import { forwardRef } from "react";
import { TbX } from "react-icons/tb";
import Avatar from "./Avatar";
import GroupAvatar from "./GroupAvatar";

export interface RecipientChipProps {
  /** Person name shown as primary label. */
  name: string;
  /** Optional avatar tone color (passed to <Avatar tone> from ds). */
  tone?: string;
  /** Optional avatar src for image variant. */
  avatarSrc?: string;
  /** "(Myself)" / "(N members)" suffix style. */
  suffix?: string;
  /** Optional remove handler — when present, renders the × button. */
  onRemove?: () => void;
  /** Optional click handler for the chip body — e.g. open the recipient picker. */
  onClick?: () => void;
  /** Use GroupAvatar (purple) instead of Avatar — for group recipients. */
  isGroup?: boolean;
  /** When isGroup, count to show in GroupAvatar. */
  groupCount?: number;
}

const RecipientChip = forwardRef<HTMLSpanElement, RecipientChipProps>(
  (
    { name, tone, avatarSrc, suffix, onRemove, onClick, isGroup, groupCount },
    ref,
  ) => {
    return (
      <Box
        as="span"
        ref={ref}
        display="inline-flex"
        alignItems="center"
        gap="1xs" // 6px
        // Asymmetric padding — see header comment. Inline literal (no token at 5).
        padding="5px 12px 5px 6px"
        bg="neutral.0"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
        borderRadius="full"
        fontSize="md" // 14px
        fontWeight={500}
        color="neutral.950"
        transition="border-color 120ms"
        // Clicking the chip body opens the recipient picker (when onClick set).
        onClick={onClick}
        cursor={onClick ? "pointer" : undefined}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        _hover={{ borderColor: "border.hover" }}
      >
        {isGroup ? (
          <GroupAvatar count={groupCount ?? 0} size="sm" />
        ) : (
          <Avatar name={name} tone={tone} src={avatarSrc} size="sm" />
        )}

        {/* .ra-recip-chip__name */}
        <Box as="span" color="neutral.950" fontWeight={500}>
          {name}
        </Box>

        {/* .ra-recip-chip__myself — muted suffix (e.g. "(Myself)" / "(3 members)").
            tabular-nums on the suffix avoids a layout jump when the member
            count flips between e.g. "(9)" and "(10)". */}
        {suffix && (
          <Box
            as="span"
            color="neutral.600"
            ml="2xs" // 4px
            fontSize="smd" // 13px
            fontWeight={400}
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {suffix}
          </Box>
        )}

        {/* .ra-recip-chip__remove — visually 18×18 but extended to 24×24
            hit area (chip is only 24-ish px tall, so 24 is the achievable
            ceiling). Icon stays 12px. */}
        {onRemove && (
          <Box
            as="button"
            type="button"
            aria-label={`Remove ${name}`}
            onClick={(e) => {
              // Don't let the × also trigger the chip's open-picker onClick.
              e.stopPropagation();
              onRemove();
            }}
            width="24px"
            height="24px"
            borderRadius="full"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            color="neutral.500"
            bg="transparent"
            border="none"
            cursor="pointer"
            ml="2xs" // 4px
            transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
            _hover={{ bg: "neutral.100", color: "neutral.900" }}
            _active={{ transform: "scale(0.96)" }}
          >
            <TbX size={12} />
          </Box>
        )}
      </Box>
    );
  },
);

RecipientChip.displayName = "RecipientChip";

export default RecipientChip;
